import { createContext, useCallback, useContext, useEffect, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";

const AuthContext = createContext(null);

export const landingPath = (me) => {
  if (me?.onboarding_completed === false) return "/onboarding";
  if (me?.sandbox && me?.space === "business") return "/entreprise";
  if (me?.partner && !me?.households?.length) return "/partenaires";
  if (me?.households?.length) return "/";
  if (me?.platform_role) return "/admin";
  if (me?.organizations?.length) return "/pro";
  if (me?.companies?.length) return "/entreprise";
  return "/";
};

export function AuthProvider({ children }) {
  const [user, setUserState] = useState(null); // null = checking, false = anonymous
  const queryClient = useQueryClient();
  const setUser = useCallback((next) => setUserState(next), []);
  const startSession = (data) => { queryClient.clear(); setUserState(data); return data; };

  const reload = useCallback(async () => {
    try {
      const { data } = await api.get("/auth/me");
      setUserState(data);
      return data;
    } catch {
      setUserState(false);
      return false;
    }
  }, []);

  useEffect(() => {
    reload();
  }, [reload]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    if (data.mfa_required) return data;
    return startSession(data);
  };
  const verifyMfa = async (mfa_token, code) => {
    const { data } = await api.post("/auth/mfa/verify", { mfa_token, code });
    return startSession(data);
  };
  const demoLogin = async (account) => {
    const { data } = await api.post("/auth/demo-login", { account });
    return startSession(data);
  };
  const startDemo = async (space) => {
    const { data } = await api.post("/demo/start", { space });
    return startSession(data);
  };
  const register = async (payload) => {
    const { data } = await api.post("/auth/register", payload);
    return startSession(data);
  };
  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    queryClient.clear();
    setUserState(false);
  };

  return (
    <AuthContext.Provider value={{ user, setUser, reload, login, verifyMfa, demoLogin, startDemo, register, logout }}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => useContext(AuthContext);
